<script setup lang="ts">
import {
	computed,
	onBeforeUnmount,
	onMounted,
	PropType,
	toRef,
	ref,
	watch,
} from "vue";
import VerticalOrDimensionSelection, {
	AxisItem,
} from "@/modules/explorer/components/AxisConfiguration/Selection.vue";

import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import { useExplorerStore } from "@/modules/explorer/store";
import { effectiveState } from "@/Utils/proxy/DerivedModelProxyHandler";
import DimensionDO = App.Data.DimensionDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import { GRAPH_TYPES } from "chartjs-graph";
import { useTabCycling } from "@/ui/composables/useTabCycling";
import Modal from "@/ui/components/modal.vue";
import { t } from "@/plugins/i18n";
import { summarizeSelection } from "./summarizeSelection";

const activeAxisTab = defineModel<
	"vertical" | "horizontal" | "offset" | "legend" | "secondary-vertical"
>();

const props = defineProps({
	isVisible: {
		type: Boolean,
		require: true,
	},
	insertItem: {
		type: Object as PropType<{
			index: number | null;
			type: "metrics" | "dimensions";
			dataTableToEdit: any;
			dataTableIndex: number;
		}>,
		required: false,
	},
});
const emit = defineEmits(["close-dialog"]);

const insertItem = toRef(props, "insertItem");

const search = ref("");

const dataTableToEdit = computed(() => insertItem.value?.data_table_to_edit);

const visible = computed({
	get() {
		return props.isVisible;
	},
	set(newValue) {
		if (!newValue) {
			closeDialog();
		}
	},
});

const closeDialog = () => {
	emit("close-dialog");
};

// "secondary-vertical" lives under the vertical tab's dropdown and has no own
// entry in AxisTabs, so cycle from the vertical tab's position instead.
const cyclingTab = computed({
	get: () =>
		activeAxisTab.value === "secondary-vertical"
			? "vertical"
			: activeAxisTab.value,
	set: (value) => {
		activeAxisTab.value = value;
	},
});

const { cycleAxisTab } = useTabCycling(() => AxisTabs.value, cyclingTab);

const baseAxisTabs = [
	{
		nameClean: "vertical",
		translationKey: "vertical",
		icon: "mdi-arrow-up",
		selectMode: "single",
	},
	{
		nameClean: "horizontal",
		translationKey: "horizontal",
		icon: "mdi-arrow-right",
		selectMode: "single",
	},
	{
		nameClean: "offset",
		translationKey: "offset",
		icon: "mdi-set-split",
		selectMode: "single",
	},
	{
		nameClean: "legend",
		translationKey: "legend",
		icon: "mdi-chart-pie",
		selectMode: "single",
	},
] as const;

const disableOffsetTab = (tabName: string): boolean => {
	return docGraphType.value === GRAPH_TYPES.AREA && tabName === "offset";
};

// Tab is handled on window (capture) rather than on the dialog card: after
// clicking a radio or a menu item, focus can land on document.body (macOS
// browsers don't focus clicked controls), where a card-scoped keydown handler
// would never fire and the first Tab press would be swallowed by the dialog's
// focus trap. Selection.vue handles its arrow keys the same way.
const handleTabKeydown = (event: KeyboardEvent) => {
	if (!props.isVisible || event.key !== "Tab") {
		return;
	}
	cycleAxisTab(event);
};

onMounted(() => {
	window.addEventListener("keydown", handleTabKeydown, true);
});

onBeforeUnmount(() => {
	window.removeEventListener("keydown", handleTabKeydown, true);
});

const AxisTabs = computed(() => {
	let tabs: readonly (typeof baseAxisTabs)[number][] = baseAxisTabs;

	if (props.insertItem) {
		const type = props.insertItem.type;

		if (type === "metrics") {
			tabs = baseAxisTabs.filter((tab) => tab.nameClean === "vertical");
		} else if (type === "dimensions") {
			tabs = baseAxisTabs.filter((tab) => tab.nameClean === "horizontal");
		}
	}

	return tabs.map((tab) => ({
		...tab,
		disabled: disableOffsetTab(tab.nameClean),
	}));
});

// pass function with logic to update selection?

export type AxisTabsData = {
	[K in (typeof AxisTabs)[number]["nameClean"]]: AxisItem[];
};

const items = computed<AxisItem[]>(() => {
	const propertyStore = usePropertyStore();
	if (props.insertItem) {
		if (activeAxisTab.value === "vertical") {
			return propertyStore.verticalsForDataTable;
		}
	}
	if (
		activeAxisTab.value === "vertical" ||
		activeAxisTab.value === "secondary-vertical"
	) {
		return propertyStore.verticalsForGraph;
	}

	return propertyStore.dimensions;
});

const docVertical = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"vertical"
);
const docSecondaryVertical = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"secondary_vertical"
);
const docHorizontal = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"horizontal_dimension"
);
const docOffset = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"offset_dimension"
);
const docLegend = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"legend_dimension"
);
const docDataTables = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"data_tables"
);

const docGraphType = effectiveState(
	() => useExplorerStore().document,
	() => useExplorerStore().derivedDocument,
	"graph_type"
);

const documentAttribute = computed<
	| DocumentVerticalDTO
	| DocumentDimensionDTO
	| DocumentVerticalDTO[]
	| DocumentDimensionDTO[]
>({
	get() {
		if (props.insertItem) {
			switch (activeAxisTab.value) {
				case "horizontal":
					return docDataTables.value[props.insertItem.data_table_index]
						.dimensions;
				case "vertical":
					return docDataTables.value[props.insertItem.data_table_index].metrics;
			}
		}
		switch (activeAxisTab.value) {
			case "vertical":
				return docVertical.value;
			case "secondary-vertical":
				return docSecondaryVertical.value;
			case "horizontal":
				return docHorizontal.value;
			case "offset":
				return docOffset.value;
			case "legend":
				return docLegend.value;
		}
		return null;
	},
	set(newValue) {
		if (props.insertItem) {
			const insertAt = props.insertItem.index + 1;
			const dataTable = dataTableToEdit.value;

			const dimCount = dataTable.dimensions.length;

			let targetArray;
			let internalIndex;

			if (props.insertItem.type === "dimensions") {
				targetArray = dataTable.dimensions;
				internalIndex = insertAt;
			} else if (props.insertItem.type === "metrics") {
				targetArray = dataTable.metrics;
				internalIndex = insertAt - dimCount;
			}

			targetArray.splice(internalIndex, 0, newValue);

			if (props.insertItem.type === "metrics") {
				docDataTables.value = docDataTables.value.map((table, index) => {
					if (index === props.insertItem.data_table_index) {
						return {
							...table,
							metrics: targetArray as DocumentVerticalDTO[],
						};
					}
				});
			} else if (props.insertItem.type === "dimensions") {
				docDataTables.value = docDataTables.value.map((table, index) => {
					if (index === props.insertItem.data_table_index) {
						return {
							...table,
							dimensions: targetArray as DimensionDO[],
						};
					}
				});
			}
		} else {
			switch (activeAxisTab.value) {
				case "vertical":
					docVertical.value = newValue as DocumentVerticalDTO;
					break;
				case "secondary-vertical":
					docSecondaryVertical.value = newValue as DocumentVerticalDTO;
					break;
				case "horizontal":
					docHorizontal.value = newValue as DimensionDO;
					break;
				case "offset":
					docOffset.value = newValue as DimensionDO;
					break;
				case "legend":
					docLegend.value = newValue as DimensionDO;
					break;
			}
		}
	},
});

const dialogTitle = computed(() =>
	props.insertItem
		? t(`axisDialog.titleInsert.${props.insertItem.type}`)
		: t("axisDialog.title")
);

const axisLabel = computed(() =>
	props.insertItem
		? t(`axisDialog.axis.${props.insertItem.type}`)
		: t(`axisDialog.axis.${activeAxisTab.value}`)
);

const tabLabel = (nameClean: string): string => {
	if (props.insertItem) {
		return t(`axisDialog.axis.${props.insertItem.type}`);
	}

	// The vertical tab covers both verticals, so it names whichever is open.
	if (nameClean === "vertical") {
		return activeAxisTab.value === "secondary-vertical"
			? t("axisDialog.axis.secondary-vertical")
			: t("axisDialog.axis.vertical");
	}

	return t(`axisDialog.axis.${nameClean}`);
};

const selectionLabel = computed(() =>
	summarizeSelection(documentAttribute.value, { t })
);

watch(activeAxisTab, (ov, nv) => {
	// Only reset when going from a vertical to a non-vertical
	if (ov?.includes("vertical") !== nv?.includes("vertical")) {
		search.value = "";
	}
});
</script>

<template>
	<modal
		v-model:visible="visible"
		:title="dialogTitle"
		icon="fa-regular fa-sliders"
		width="52rem"
		no-body-padding
		:persistent="false"
	>
		<v-tabs
			v-model="cyclingTab"
			class="axis-tabs flex-shrink-0"
			bg-color="secondary"
			height="40"
			center-active
			show-arrows
		>
			<v-tab
				v-for="axis in AxisTabs"
				:disabled="axis.disabled"
				:key="axis.nameClean"
				:value="axis.nameClean"
				class="axis-tab"
			>
				<v-icon v-if="!insertItem" :icon="axis.icon" size="16" />
				<span>{{ tabLabel(axis.nameClean) }}</span>
				<v-menu v-if="axis.nameClean === 'vertical' && !insertItem">
					<template v-slot:activator="{ props: menu }">
						<span
							v-bind="menu"
							class="d-inline-flex"
							data-testid="vertical-menu-icon"
						>
							<v-icon icon="mdi-menu-down" size="18" />
						</span>
					</template>
					<v-list color="background" density="compact">
						<v-list-item
							@click="activeAxisTab = 'vertical'"
							data-testid="primary-vertical-list-item"
						>
							{{ t("axisDialog.axis.verticalPrimary") }}
						</v-list-item>
						<v-list-item
							@click="activeAxisTab = 'secondary-vertical'"
							data-testid="secondary-vertical-list-item"
						>
							{{ t("axisDialog.axis.verticalSecondary") }}
						</v-list-item>
					</v-list>
				</v-menu>
			</v-tab>
		</v-tabs>
		<div class="body">
			<vertical-or-dimension-selection
				v-if="!props.insertItem"
				v-model="documentAttribute"
				v-model:search="search"
				:axis-name-clean="activeAxisTab"
				:items="items"
				:document="useExplorerStore().document"
			/>
			<vertical-or-dimension-selection
				v-if="documentAttribute != null && props.insertItem"
				v-model="documentAttribute"
				v-model:search="search"
				:data-table="dataTableToEdit"
				:axis-name-clean="
					activeAxisTab === 'vertical' ? 'metrics' : 'horizontal'
				"
				:items="items"
				:document="useExplorerStore().document"
				:has-multiple-selected="true"
			/>
		</div>
		<template #actions>
			<div class="footer">
				<p class="summary text-body-2">
					<span class="seg-axis">{{ axisLabel }}</span>
					<span class="seg-selection">{{ selectionLabel }}</span>
				</p>
				<v-btn variant="text" @click="closeDialog">
					{{ t("axisDialog.action.close") }}
				</v-btn>
			</div>
		</template>
	</modal>
</template>

<style scoped lang="scss">
/* Two panes that each scroll on their own, like the FilterDialog body. */
.body {
	display: flex;
	height: 64vh;
	min-height: 0;
	overflow: hidden;
}

.axis-tabs {
	:deep(.v-tab.axis-tab) {
		min-width: 0;
		padding-inline: 0.75rem;
		text-transform: none;
		letter-spacing: normal;
		font-size: 0.875rem;

		.v-btn__content {
			gap: 0.375rem;
		}
	}
}

.footer {
	display: flex;
	align-items: center;
	gap: 1rem;
	width: 100%;
}

.summary {
	flex: 1 1 auto;
	display: flex;
	align-items: baseline;
	gap: 0.375rem;
	margin: 0;
	min-width: 0;
	color: rgb(var(--color-on-background));
}

.seg-axis::after {
	content: ":";
}

.seg-selection {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
